import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  ReviewEvent,
  ReviewPeriodData,
  ReviewReason,
  ReviewTimelineTick,
  TargetedReviewPeriod,
} from "src/store/api/generatedApi";
import {
  CONSUMPTION_EVENT_FILTER,
  matchesEventFilter,
} from "components/manualReview/filters/eventFilters";
import {
  dedupeReviewEvents,
  stableReviewEventId,
} from "components/manualReview/utils/eventUtils";
import { findLabelValue } from "components/manualReview/utils/formatters";
import { DEFAULT_WINDOW_MINUTES } from "components/manualReview/hooks/useManualReviewNavigation";
import {
  FLAG_OUTLINE_COLOR,
  getHighestPriorityReasonConfig,
  highlightColorsByEventId,
  TARGETED_REVIEW_REASONS,
} from "components/manualReview/utils/reviewReasons";
import { sorted } from "helpers";

dayjs.extend(utc);

export const isFlaggedForTargetedReview = (event: {
  review_reasons: ReviewReason[];
}): boolean =>
  event.review_reasons.some((reason) =>
    TARGETED_REVIEW_REASONS.includes(reason)
  );

// The colour for an event's single most severe targeted-review reason, for
// a caller that can only show one colour per event, e.g. a timeline bar's
// outline.
export const getTargetedReviewReasonColor = (event: {
  review_reasons: ReviewReason[];
}): string | undefined =>
  getHighestPriorityReasonConfig(
    event.review_reasons.filter((reason) =>
      TARGETED_REVIEW_REASONS.includes(reason)
    )
  )?.themeColor;

export const getTargetedReviewHighlightColor = (event: {
  review_reasons: ReviewReason[];
}): string | undefined =>
  isFlaggedForTargetedReview(event) ? FLAG_OUTLINE_COLOR : undefined;

export const targetedReviewHighlightColors = (
  events: readonly { id: string; review_reasons: ReviewReason[] }[]
): ReadonlyMap<string, string | null> =>
  highlightColorsByEventId(events, getTargetedReviewHighlightColor);

export interface TargetedReviewFlagBreakdown {
  reason: ReviewReason;
  eventCount: number;
  materials: string[];
}

export type RowConsumption = "consumption" | "non_consumption";

export interface TargetedReviewPeriodRow {
  id: string;
  start: Dayjs;
  end: Dayjs;
  flaggedCount: number;
  selectedRandomly: boolean;
  flagBreakdown: TargetedReviewFlagBreakdown[];
  periodEvents: ReviewEvent[];
  flaggedEventIds: string[];
  reviewPeriod: ReviewPeriodData | null;
  consumption: RowConsumption;
}

const buildFlagBreakdown = (
  flaggedEvents: ReviewEvent[]
): TargetedReviewFlagBreakdown[] =>
  TARGETED_REVIEW_REASONS.flatMap((reason) => {
    const eventsWithReason = flaggedEvents.filter((reviewEvent) =>
      reviewEvent.review_reasons.includes(reason)
    );
    if (eventsWithReason.length === 0) return [];
    const materials = [
      ...new Set(
        eventsWithReason
          .flatMap((reviewEvent) => [
            findLabelValue(reviewEvent.merged.labels, "material"),
            findLabelValue(reviewEvent.merged.labels, "co_material"),
          ])
          .filter((material): material is string => material !== null)
      ),
    ];
    return [
      {
        reason,
        eventCount: eventsWithReason.length,
        materials: sorted(materials, (material) => material),
      },
    ];
  });

interface ConsumptionTickIndex {
  eventIds: Set<number>;
  verifiedStarts: number[];
}

const buildConsumptionTickIndex = (
  ticks: ReviewTimelineTick[]
): ConsumptionTickIndex => {
  const consumptionTicks = ticks.filter((tick) =>
    matchesEventFilter(tick, CONSUMPTION_EVENT_FILTER)
  );
  return {
    eventIds: new Set(consumptionTicks.map((tick) => tick.id)),
    verifiedStarts: consumptionTicks
      .filter((tick) => tick.verified)
      .map((tick) => dayjs.utc(tick.start).valueOf())
      .sort((first, second) => first - second),
  };
};

const firstIndexAtOrAfter = (
  sortedValues: number[],
  target: number
): number => {
  let low = 0;
  let high = sortedValues.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if ((sortedValues[middle] ?? Infinity) < target) low = middle + 1;
    else high = middle;
  }
  return low;
};

const hasSortedValueInRange = (
  sortedValues: number[],
  lowerInclusive: number,
  upperExclusive: number
): boolean => {
  const index = firstIndexAtOrAfter(sortedValues, lowerInclusive);
  return (sortedValues[index] ?? Infinity) < upperExclusive;
};

const rowConsumptionFromIndex = ({
  row,
  index,
}: {
  row: Pick<TargetedReviewPeriodRow, "periodEvents" | "start" | "end">;
  index: ConsumptionTickIndex;
}): RowConsumption => {
  const hasMatchedOriginalEvent = row.periodEvents.some((periodEvent) =>
    periodEvent.original_events.some((originalEvent) =>
      index.eventIds.has(originalEvent.id)
    )
  );
  if (hasMatchedOriginalEvent) return "consumption";

  const hasVerifiedTickInRange = hasSortedValueInRange(
    index.verifiedStarts,
    row.start.valueOf(),
    row.end.valueOf()
  );
  return hasVerifiedTickInRange ? "consumption" : "non_consumption";
};

export const rowConsumption = ({
  row,
  ticks,
}: {
  row: Pick<TargetedReviewPeriodRow, "periodEvents" | "start" | "end">;
  ticks: ReviewTimelineTick[];
}): RowConsumption =>
  rowConsumptionFromIndex({ row, index: buildConsumptionTickIndex(ticks) });

export type ConsumptionFilter = "all" | "consumption" | "non_consumption";

export const reviewPeriodsByStart = (
  reviewPeriods: ReviewPeriodData[]
): Map<string, ReviewPeriodData> =>
  new Map(
    reviewPeriods.map((period) => [
      dayjs.utc(period.start).toISOString(),
      period,
    ])
  );

export const buildTargetedReviewPeriodRows = ({
  targetedReviewPeriods,
  reviewEvents,
  reviewPeriods,
  ticks,
}: {
  targetedReviewPeriods: TargetedReviewPeriod[];
  reviewEvents: ReviewEvent[];
  reviewPeriods: ReviewPeriodData[];
  ticks: ReviewTimelineTick[];
}): TargetedReviewPeriodRow[] => {
  const periodByStart = reviewPeriodsByStart(reviewPeriods);
  const consumptionTickIndex = buildConsumptionTickIndex(ticks);
  const uniqueReviewEvents = dedupeReviewEvents(reviewEvents);
  return targetedReviewPeriods.map((period) => {
    const start = dayjs.utc(period.start);
    const end = dayjs.utc(period.end);
    const periodEvents = uniqueReviewEvents.filter((reviewEvent) => {
      const eventStart = dayjs.utc(reviewEvent.merged.event.start);
      return !eventStart.isBefore(start) && eventStart.isBefore(end);
    });
    const flaggedEvents = periodEvents.filter(isFlaggedForTargetedReview);
    return {
      id: start.toISOString(),
      start,
      end,
      flaggedCount: period.flagged_count,
      selectedRandomly: period.random_review === "selected",
      flagBreakdown: buildFlagBreakdown(flaggedEvents),
      periodEvents,
      flaggedEventIds: flaggedEvents.map(stableReviewEventId),
      reviewPeriod: periodByStart.get(start.toISOString()) ?? null,
      consumption: rowConsumptionFromIndex({
        row: { periodEvents, start, end },
        index: consumptionTickIndex,
      }),
    };
  });
};

export interface LastSave {
  dayReviewEvents: ReviewEvent[];
  dayReviewPeriods: ReviewPeriodData[];
}

export const hasDataFromAfterSave = ({
  lastSave,
  dayReviewEvents,
  dayReviewPeriods,
  isFetching,
}: {
  lastSave: LastSave | null;
  dayReviewEvents: ReviewEvent[];
  dayReviewPeriods: ReviewPeriodData[];
  isFetching: boolean;
}): boolean => {
  if (isFetching) return false;
  if (lastSave === null) return true;
  return (
    dayReviewEvents !== lastSave.dayReviewEvents ||
    dayReviewPeriods !== lastSave.dayReviewPeriods
  );
};

export const mergeSessionRows = ({
  sessionRows,
  liveReviewPeriodByStart,
  liveRowById,
}: {
  sessionRows: Map<string, TargetedReviewPeriodRow>;
  liveReviewPeriodByStart: Map<string, ReviewPeriodData>;
  liveRowById: Map<string, TargetedReviewPeriodRow>;
}): TargetedReviewPeriodRow[] => {
  const sessionOnlyRows = [...sessionRows.values()]
    .filter((row) => !liveRowById.has(row.id))
    .map((row) => ({
      ...row,
      reviewPeriod: liveReviewPeriodByStart.get(row.id) ?? row.reviewPeriod,
    }));

  const mergedLiveRows = [...liveRowById.values()].map((row) => {
    const snapshot = sessionRows.get(row.id);
    if (!snapshot) return row;
    if (row.flagBreakdown.length === 0) {
      return {
        ...row,
        flagBreakdown: snapshot.flagBreakdown,
        flaggedEventIds: snapshot.flaggedEventIds,
        consumption: snapshot.consumption,
      };
    }
    const liveReasons = new Set(row.flagBreakdown.map((entry) => entry.reason));
    const missingFromLive = snapshot.flagBreakdown.filter(
      (entry) => !liveReasons.has(entry.reason)
    );
    return {
      ...row,
      flagBreakdown: [...row.flagBreakdown, ...missingFromLive],
      flaggedEventIds: [
        ...new Set([...row.flaggedEventIds, ...snapshot.flaggedEventIds]),
      ],
      consumption: snapshot.consumption,
    };
  });

  return sorted([...mergedLiveRows, ...sessionOnlyRows], (row) =>
    row.start.valueOf()
  );
};

export const sessionRowsToKeep = ({
  displayRows,
  settledIds,
}: {
  displayRows: TargetedReviewPeriodRow[];
  settledIds: string[];
}): TargetedReviewPeriodRow[] =>
  displayRows.filter((row) => settledIds.includes(row.id));

export const canOpenRow = ({
  row,
  settledIds,
  liveRowById,
  liveReviewPeriodByStart,
  hasFreshData,
}: {
  row: TargetedReviewPeriodRow;
  settledIds: Set<string>;
  liveRowById: Map<string, TargetedReviewPeriodRow>;
  liveReviewPeriodByStart: Map<string, ReviewPeriodData>;
  hasFreshData: boolean;
}): boolean =>
  settledIds.has(row.id)
    ? hasFreshData && liveReviewPeriodByStart.has(row.id)
    : liveRowById.has(row.id);

export const unverifiedAgainIds = ({
  settledIds,
  liveReviewPeriodByStart,
  hasFreshData,
}: {
  settledIds: Set<string>;
  liveReviewPeriodByStart: Map<string, ReviewPeriodData>;
  hasFreshData: boolean;
}): Set<string> =>
  hasFreshData
    ? new Set(
        [...settledIds].filter((id) => {
          const livePeriod = liveReviewPeriodByStart.get(id);
          return livePeriod !== undefined && livePeriod.verified_at == null;
        })
      )
    : new Set();

export type TargetedReviewOutcome = "verified" | "skipped" | "broughtIn";

export interface TargetedReviewDisposition {
  periodId: string;
  outcome: TargetedReviewOutcome;
}

export interface TargetedReviewPeriodGroup {
  id: string;
  start: Dayjs;
  end: Dayjs;
  rowIds: string[];
  rows: TargetedReviewPeriodRow[];
}

export const isRowDone = ({
  row,
  settledIds,
}: {
  row: TargetedReviewPeriodRow;
  settledIds: Set<string>;
}): boolean => row.reviewPeriod?.verified_at != null || settledIds.has(row.id);

export const isRandomPickOnly = (row: TargetedReviewPeriodRow): boolean =>
  row.selectedRandomly && row.flaggedCount === 0;

const HALF_HOUR_BUCKET_MS = DEFAULT_WINDOW_MINUTES * 60 * 1000;

const halfHourBucket = (time: Dayjs): number =>
  Math.floor(time.valueOf() / HALF_HOUR_BUCKET_MS);

const startsNewGroup = ({
  previousRow,
  row,
  settledIds,
  grouping,
}: {
  previousRow: TargetedReviewPeriodRow | undefined;
  row: TargetedReviewPeriodRow;
  settledIds: Set<string>;
  grouping: boolean;
}): boolean => {
  if (!grouping || !previousRow) return true;
  return (
    previousRow.end.valueOf() !== row.start.valueOf() ||
    halfHourBucket(previousRow.start) !== halfHourBucket(row.start) ||
    previousRow.consumption !== row.consumption ||
    isRandomPickOnly(previousRow) !== isRandomPickOnly(row) ||
    isRowDone({ row: previousRow, settledIds }) !==
      isRowDone({ row, settledIds })
  );
};

export const groupRows = ({
  rows,
  settledIds,
  grouping,
}: {
  rows: TargetedReviewPeriodRow[];
  settledIds: Set<string>;
  grouping: boolean;
}): TargetedReviewPeriodGroup[] =>
  rows.reduce<TargetedReviewPeriodGroup[]>((groups, row) => {
    const previousGroup = groups[groups.length - 1];
    const newGroup: TargetedReviewPeriodGroup = {
      id: `group:${row.id}`,
      start: row.start,
      end: row.end,
      rowIds: [row.id],
      rows: [row],
    };
    if (!previousGroup) return [...groups, newGroup];

    const previousRow = previousGroup.rows[previousGroup.rows.length - 1];
    if (startsNewGroup({ previousRow, row, settledIds, grouping })) {
      return [...groups, newGroup];
    }

    return [
      ...groups.slice(0, -1),
      {
        ...previousGroup,
        end: row.end,
        rowIds: [...previousGroup.rowIds, row.id],
        rows: [...previousGroup.rows, row],
      },
    ];
  }, []);

export const groupMatchesConsumptionFilter = (
  group: TargetedReviewPeriodGroup,
  filter: ConsumptionFilter
): boolean =>
  filter === "all" || group.rows.some((row) => row.consumption === filter);

export const mergeFlagBreakdowns = (
  breakdowns: TargetedReviewFlagBreakdown[][]
): TargetedReviewFlagBreakdown[] => {
  const eventCountByReason = new Map<ReviewReason, number>();
  const materialsByReason = new Map<ReviewReason, Set<string>>();
  breakdowns.flat().forEach((entry) => {
    eventCountByReason.set(
      entry.reason,
      (eventCountByReason.get(entry.reason) ?? 0) + entry.eventCount
    );
    const materials = materialsByReason.get(entry.reason) ?? new Set<string>();
    entry.materials.forEach((material) => materials.add(material));
    materialsByReason.set(entry.reason, materials);
  });
  return TARGETED_REVIEW_REASONS.flatMap((reason) => {
    const eventCount = eventCountByReason.get(reason);
    if (eventCount === undefined) return [];
    const materials = [...(materialsByReason.get(reason) ?? [])];
    return [
      {
        reason,
        eventCount,
        materials: sorted(materials, (material) => material),
      },
    ];
  });
};

export const unexplainedFlaggedCount = (
  rows: TargetedReviewPeriodRow[]
): number =>
  rows
    .filter((row) => row.flagBreakdown.length === 0)
    .reduce((sum, row) => sum + row.flaggedCount, 0);

export type RowDisposition =
  "verifiedLinked" | "reviewed" | "skipped" | "notReviewed";

export const dispositionForRow = ({
  row,
  reviewedIds,
  broughtInIds,
  seenIds,
}: {
  row: TargetedReviewPeriodRow;
  reviewedIds: Set<string>;
  broughtInIds: Set<string>;
  seenIds: Set<string>;
}): RowDisposition => {
  if (broughtInIds.has(row.id)) return "verifiedLinked";
  if (reviewedIds.has(row.id) || row.reviewPeriod?.verified_at)
    return "reviewed";
  if (seenIds.has(row.id)) return "skipped";
  return "notReviewed";
};

export type GroupStatus = RowDisposition;

export const groupStatus = ({
  group,
  reviewedIds,
  broughtInIds,
  seenIds,
}: {
  group: TargetedReviewPeriodGroup;
  reviewedIds: Set<string>;
  broughtInIds: Set<string>;
  seenIds: Set<string>;
}): GroupStatus => {
  const dispositions = group.rows.map((row) =>
    dispositionForRow({ row, reviewedIds, broughtInIds, seenIds })
  );
  if (dispositions.every((disposition) => disposition === "verifiedLinked")) {
    return "verifiedLinked";
  }
  if (
    dispositions.every(
      (disposition) =>
        disposition === "reviewed" || disposition === "verifiedLinked"
    )
  ) {
    return "reviewed";
  }
  if (dispositions.every((disposition) => disposition === "skipped")) {
    return "skipped";
  }
  return "notReviewed";
};

export const nextGroupToReview = ({
  groups,
  after,
  seenIds,
}: {
  groups: TargetedReviewPeriodGroup[];
  after: Dayjs;
  seenIds: Set<string>;
}): TargetedReviewPeriodGroup | undefined => {
  const isCandidate = (group: TargetedReviewPeriodGroup) =>
    group.rows.some(
      (row) => !seenIds.has(row.id) && row.reviewPeriod?.verified_at == null
    );
  const afterValue = after.valueOf();
  return (
    groups.find(
      (group) => group.start.valueOf() >= afterValue && isCandidate(group)
    ) ?? groups.find(isCandidate)
  );
};

export const nextVisibleGroupToReview = ({
  rows,
  settledIds,
  grouping,
  consumptionFilter,
  after,
  seenIds,
}: {
  rows: TargetedReviewPeriodRow[];
  settledIds: Set<string>;
  grouping: boolean;
  consumptionFilter: ConsumptionFilter;
  after: Dayjs;
  seenIds: Set<string>;
}): TargetedReviewPeriodGroup | undefined =>
  nextGroupToReview({
    groups: groupRows({ rows, settledIds, grouping }).filter((group) =>
      groupMatchesConsumptionFilter(group, consumptionFilter)
    ),
    after,
    seenIds,
  });

export const settledIdsAfterDispositions = ({
  settledIds,
  dispositions,
}: {
  settledIds: Set<string>;
  dispositions: TargetedReviewDisposition[];
}): Set<string> =>
  new Set([
    ...settledIds,
    ...dispositions
      .filter((disposition) => disposition.outcome !== "skipped")
      .map((disposition) => disposition.periodId),
  ]);

export const groupRowDispositions = (
  group: TargetedReviewPeriodGroup,
  outcome: TargetedReviewOutcome
): TargetedReviewDisposition[] =>
  group.rowIds.map((periodId) => ({ periodId, outcome }));

export const dispositionsForGroupSave = ({
  group,
  verifiedPeriods,
}: {
  group: TargetedReviewPeriodGroup;
  verifiedPeriods: ReviewPeriodData[];
}): TargetedReviewDisposition[] => {
  const groupRowIds = new Set(group.rowIds);
  const broughtInIds = new Set(
    verifiedPeriods
      .map((verifiedPeriod) => dayjs.utc(verifiedPeriod.start).toISOString())
      .filter((periodId) => !groupRowIds.has(periodId))
  );
  return [
    ...groupRowDispositions(group, "verified"),
    ...[...broughtInIds].map((periodId): TargetedReviewDisposition => ({
      periodId,
      outcome: "broughtIn",
    })),
  ];
};

export interface FrozenGroup {
  rowIds: string[];
  start: Dayjs;
  end: Dayjs;
}

export const resolveFrozenGroupRows = ({
  frozen,
  rowById,
}: {
  frozen: FrozenGroup;
  rowById: Map<string, TargetedReviewPeriodRow>;
}): TargetedReviewPeriodRow[] =>
  frozen.rowIds
    .map((rowId) => rowById.get(rowId))
    .filter((row): row is TargetedReviewPeriodRow => row !== undefined);

export const pruneFrozenGroup = ({
  frozen,
  rowById,
}: {
  frozen: FrozenGroup;
  rowById: Map<string, TargetedReviewPeriodRow>;
}): FrozenGroup | undefined => {
  const rowIds = frozen.rowIds.filter((rowId) => rowById.has(rowId));
  if (rowIds.length === frozen.rowIds.length) return frozen;
  return rowIds.length > 0 ? { ...frozen, rowIds } : undefined;
};
